import { useResizeObserver } from "@amzn/stencil-react-components/hooks";
import { Col, View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { SearchResult } from "common/components";
import { CatalogItemSimplified } from "common/types";
import { PERMISSIONS } from "config";
import { UserPermissions } from "lib/services";
import React, { FC, useLayoutEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

export * from "./reducer";

export interface CatalogItemsListProps {
  /**
   * Function to call to verify if should load more items
   */
  shouldLoadMore: () => boolean;

  /**
   * Function to call on the need to searh more items
   */
  onSearchNext: () => void;

  /**
   * Id of the container to view
   */
  scrollableTarget: string;

  /**
   * Should use the new search experience
   */
  useSearchExperience?: boolean;

  /**
   * List of items to show
   */
  items: CatalogItemSimplified[] | undefined;

  /**
   * Flag to indicate if the list is loading
   */
  isLoading: boolean;

  /**
   * Flag to indicate if the list is loading more items
   */
  isLoadingNextItems?: boolean;

  /**
   * Total count of items
   */
  totalCount: number | undefined;
}

const CatalogItemsList: FC<CatalogItemsListProps> = (props) => {
  const { ref, height } = useResizeObserver<HTMLDivElement>();
  const toggleLists = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_LISTS);

  const hasMoreItems = () => {
    return props.items && props.totalCount ? props.items.length < props.totalCount : false;
  };

  // The InfiniteScroll needs the content to overflow to work, this useEffect forces the overflow if needed
  useLayoutEffect(() => {
    if (!hasMoreItems()) return;

    // Conditions is checking if should load more items
    if (props.shouldLoadMore()) {
      props.onSearchNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, props.isLoadingNextItems, props.isLoading]);

  /* istanbul ignore next */
  const itemsComponents: React.JSX.Element[] = props.items
    ? props.items.map((item) => {
        return (
          <SearchResult
            key={`${item.itemId}`}
            taxonomyId={item.itemId || ""}
            logoUrl={item.logoImageUrl || ""}
            name={item.name || ""}
            shortDescription={item.shortDescription || ""}
            available={item.status || ""}
            useSearchExperience={props.useSearchExperience}
            tags={item.tags}
            price={item.price}
            os={item.os}
            eta={item.etaDays}
          />
        );
      })
    : [];

  return (
    <Col ref={ref}>
      <InfiniteScroll
        next={props.onSearchNext}
        dataLength={itemsComponents.length}
        hasMore={hasMoreItems()}
        loader={
          <View padding="S700">
            <Spinner size={SpinnerSize.Medium} fr={undefined} />
          </View>
        }
        scrollableTarget={props.scrollableTarget}
      >
        {itemsComponents}
      </InfiniteScroll>
    </Col>
  );
};

export default CatalogItemsList;
